/**
 * Fiscal Printer - XML receipt printing protocol (Novitus Deon Online v1.02)
 */
import { sendXML, drainPort } from './transport';
import { ensurePortWritable, beginPrinting, endPrinting } from './connection';
import { isDepositItem, prepareItemNames, mapVatRate, mapPaymentType } from './itemHelpers';
import { stripDiacritics, textToBytes } from './encoding';
import { sprawdzKontrakt, zbudujPakiety } from './receiptContract';

/**
 * Get printer status via XML DLE query.
 * Uzywa angielskiego <packet><dle/></packet> (Novitus XML protocol v1.09 sekcja 1).
 * Zwraca atrybuty: online, papererror, printererror (yes/no).
 *
 * NIE wysyla starego polskiego <pakiet><dle_pl/></pakiet> ktore wywoluje
 * "nieznany rozkaz" na drukarkach Deon Online firmware 1.40+.
 */
export async function getStatusXML(port, decoder) {
  if (!port) {
    return { connected: false, responding: false, error: 'Brak portu drukarki' };
  }
  if (!port.readable || !port.writable) {
    return { connected: false, responding: false, error: 'Port drukarki nie jest gotowy (sprawdz kabel/zasilanie)' };
  }

  try {
    const response = await sendXML(port, decoder, '<packet><dle/></packet>', true);
    if (response) {
      const onlineMatch = response.match(/online\s*=\s*"(yes|no)"/);
      const paperMatch = response.match(/papererror\s*=\s*"(yes|no)"/);
      const errorMatch = response.match(/printererror\s*=\s*"(yes|no)"/);
      if (onlineMatch || paperMatch || errorMatch) {
        return {
          connected: true,
          responding: true,
          online: onlineMatch ? onlineMatch[1] === 'yes' : true,
          noPaper: paperMatch ? paperMatch[1] === 'yes' : false,
          deviceError: errorMatch ? errorMatch[1] === 'yes' : false,
          fiscal: true,
          raw: response
        };
      }
    }
  } catch (xmlError) {
    console.warn('[FISCAL-STATUS] <dle/> failed:', xmlError?.message);
  }

  // Fail-open: port otwarty, drukarka nie odpowiedziala na <dle/> (timeout albo blad).
  // Uznajemy za polaczona z ostrzezeniem - paragon i tak prawdopodobnie dziala.
  return {
    connected: true,
    responding: true,
    online: true,
    noPaper: false,
    deviceError: false,
    fiscal: true,
    uncertain: true,
    warning: 'Drukarka nie odpowiedziala na <dle/> - sprawdz wydrukiem'
  };
}

// Helper: dopasuj atrybut yes/no | tak/nie z odpowiedzi XML (tolerancyjny na spacje wokol =).
function matchInt(response, key) {
  const m = response.match(new RegExp(`${key}\\s*=\\s*"(\\d+)"`));
  return m ? parseInt(m[1], 10) : null;
}

function matchYesNo(response, key) {
  const re = new RegExp(`${key}\\s*=\\s*"(yes|no|tak|nie)"`);
  const m = response.match(re);
  if (!m) return null;
  return m[1] === 'yes' || m[1] === 'tak';
}

// Capability cache - localStorage. Po pierwszej probie zapamietuje czy drukarka
// wspiera daną komendę. Jesli odrzucila ("blad parametru" z firmware ktora nie zna
// danego action), nie probujemy ponownie - kasjer widzi blad raz, nie przy kazdym paragonie.
const CAP_KEY = 'fiscal_capabilities';
function getCap(name) {
  try {
    const all = JSON.parse(localStorage.getItem(CAP_KEY) || '{}');
    return all[name];
  } catch { return undefined; }
}
function setCap(name, value) {
  try {
    const all = JSON.parse(localStorage.getItem(CAP_KEY) || '{}');
    all[name] = value;
    localStorage.setItem(CAP_KEY, JSON.stringify(all));
    console.log(`[FISCAL-CAPS] ${name} = ${value}`);
  } catch {}
}
export function resetFiscalCapabilities() {
  try { localStorage.removeItem(CAP_KEY); } catch {}
}

// <enq/> - stan logiczny drukarki (sekcja 2 protokolu Novitus).
async function queryEnq(port, decoder) {
  try {
    const r = await sendXML(port, decoder, '<packet><enq/></packet>', true);
    console.log('[FISCAL-VERIFY] ENQ raw response:', r);
    if (!r) return null;
    const parsed = {
      fiscal: matchYesNo(r, 'fiscal'),
      lastcommanderror: matchYesNo(r, 'lastcommanderror'),
      intransaction: matchYesNo(r, 'intransaction'),
      // UWAGA: w realnym XML atrybut nazywa sie 'lasttransactioncorrect' (z 'ion'),
      // a opis w dokumentacji pisze 'Lasttransactcorrect'. Sprawdzamy OBIE pisownie -
      // wczesniej kod szukal tylko krotszej i NIGDY nie odczytal tego pola.
      lasttransactcorrect: matchYesNo(r, 'lasttransactioncorrect') ?? matchYesNo(r, 'lasttransactcorrect'),
    };
    console.log('[FISCAL-VERIFY] ENQ parsed:', parsed);
    return { ...parsed, raw: r };
  } catch (e) {
    console.warn('[FISCAL-VERIFY] ENQ error:', e?.message);
    return null;
  }
}

// <dle2/> - rozszerzony status (sekcja 3 protokolu).
async function queryDle2(port, decoder) {
  try {
    const r = await sendXML(port, decoder, '<packet><dle2/></packet>', true);
    console.log('[FISCAL-VERIFY] DLE2 raw response:', r);
    if (!r) return null;
    const parsed = {
      online: matchYesNo(r, 'online'),
      papererror: matchYesNo(r, 'papererror'),
      printererror: matchYesNo(r, 'printererror'),
      intransaction: matchYesNo(r, 'intransaction'),
      is_printing: matchYesNo(r, 'is_printing'),
      paper_near_end: matchYesNo(r, 'paper_near_end'),
      head_open: matchYesNo(r, 'head_open'),
      // Liczniki z sekcji 3 - sluza do potwierdzenia wydruku, gdy drukarka
      // nie obsluguje <info action="lasttransaction">.
      receiptcount: matchInt(r, 'receiptcount'),
      printout_count: matchInt(r, 'printout_count'),
    };
    console.log('[FISCAL-VERIFY] DLE2 parsed:', parsed);
    return { ...parsed, raw: r };
  } catch (e) {
    console.warn('[FISCAL-VERIFY] DLE2 error:', e?.message);
    return null;
  }
}

// <info action="lasttransaction"/> - stan ostatniej transakcji (sekcja 21).
//
// KLUCZOWE rozroznienie (nie mieszac stanu przejsciowego z definitywnym):
//   - return null                    -> TRANSIENT: timeout / brak odpowiedzi / zerwane
//                                       polaczenie. NIE cache'ujemy - to sie moze udac
//                                       przy kolejnej probie (np. po reopen portu).
//   - return { unsupported: true }   -> DEFINITYWNE: drukarka odpowiedziala, ale komendy
//                                       nie zna / odrzucila ja. Cache=false, nie meczymy
//                                       LCD "bledem parametru" przy kazdym paragonie.
//   - return { state, printoutno, .. } -> poprawny odczyt ostatniej transakcji.
async function queryLastTransaction(port, decoder) {
  if (getCap('lasttransaction') === false) {
    console.log('[FISCAL-VERIFY] LASTTX skip - cached unsupported');
    return { unsupported: true };
  }
  let r;
  try {
    r = await sendXML(port, decoder, '<packet><info action="lasttransaction"></info></packet>', true);
  } catch (e) {
    const msg = e?.message || '';
    console.warn('[FISCAL-VERIFY] LASTTX error:', msg);
    // Drukarka ODRZUCILA komende (zwrocila <error code=...>) -> definitywnie nie wspiera.
    if (/odrzuci/i.test(msg)) {
      setCap('lasttransaction', false);
      return { unsupported: true };
    }
    // Inny blad = brak zapisu/odczytu portu (rozlaczenie) -> stan przejsciowy.
    return null;
  }
  console.log('[FISCAL-VERIFY] LASTTX raw response:', r);
  if (!r) {
    // Timeout / brak odpowiedzi -> PRZEJSCIOWE. NIE cache'ujemy.
    return null;
  }
  const lastTxFragment = r.match(/<info\s+action\s*=\s*"lasttransaction"[^>]*>/);
  if (!lastTxFragment) {
    // Drukarka odpowiedziala CZYMS, ale bez fragmentu lasttransaction ->
    // komenda nieobslugiwana. To definitywna (nie przejsciowa) informacja.
    setCap('lasttransaction', false);
    return { unsupported: true };
  }
  setCap('lasttransaction', true);
  const fragment = lastTxFragment[0];
  const stateMatch = fragment.match(/state\s*=\s*"(open|close|cancelled|closed)"/);
  const printoutMatch = fragment.match(/printoutno\s*=\s*"([^"]+)"/);
  const numberMatch = fragment.match(/number\s*=\s*"([^"]+)"/);
  const totalMatch = fragment.match(/total\s*=\s*"([^"]+)"/);
  const parsed = {
    state: stateMatch ? stateMatch[1] : null,
    printoutno: printoutMatch ? printoutMatch[1] : null,
    number: numberMatch ? numberMatch[1] : null,
    total: totalMatch ? totalMatch[1] : null,
  };
  console.log('[FISCAL-VERIFY] LASTTX parsed:', parsed);
  return { ...parsed, raw: r };
}

// <info action="last_daily_report"/> - sekcja 23, zwraca lastreceipt (numer ostatniego
// wydrukowanego paragonu po ostatnim raporcie Z) i receiptcount.
// UWAGA: na Deon Online 1.40 ta komenda moze nie byc obslugiwana - lazy detect + cache.
async function queryLastReceiptCounter(port, decoder) {
  if (getCap('lastDailyReport') === false) {
    console.log('[FISCAL-VERIFY] LASTREPORT skip - cached unsupported');
    return null;
  }
  try {
    const r = await sendXML(port, decoder, '<packet><info action="last_daily_report"></info></packet>', true);
    console.log('[FISCAL-VERIFY] LASTREPORT raw response:', r);
    if (!r) return null;
    const reportFragment = r.match(/<info\s+action\s*=\s*"last_daily_report"[^>]*>/);
    if (!reportFragment) {
      if (r.match(/lastcommanderror\s*=\s*"yes"/)) {
        setCap('lastDailyReport', false);
      }
      return null;
    }
    setCap('lastDailyReport', true);
    const fragment = reportFragment[0];
    const lastrecMatch = fragment.match(/lastreceipt\s*=\s*"(\d+)"/);
    const countMatch = fragment.match(/receiptcount\s*=\s*"(\d+)"/);
    if (!lastrecMatch && !countMatch) return null;
    const parsed = {
      lastreceipt: lastrecMatch ? parseInt(lastrecMatch[1], 10) : null,
      receiptcount: countMatch ? parseInt(countMatch[1], 10) : null,
    };
    console.log('[FISCAL-VERIFY] LASTREPORT parsed:', parsed);
    return parsed;
  } catch (e) {
    console.warn('[FISCAL-VERIFY] LASTREPORT error:', e?.message);
    if (e?.message?.includes('[4]') || e?.message?.includes('[1022]')) {
      setCap('lastDailyReport', false);
    }
    return null;
  }
}

// Porownanie kwot z tolerancja groszowa. false gdy ktoras nieznana.
function totalsMatch(a, b) {
  const x = parseFloat(a);
  const y = parseFloat(b);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
  return Math.abs(x - y) < 0.005;
}

function extractErrorCode(message) {
  const m = (message || '').match(/\[(\d+)\]/);
  return m ? m[1] : undefined;
}

/**
 * Auto-weryfikacja wydruku paragonu po <receipt action="close">.
 *
 * KONTRAKT (wspolny z useFiscalPrinter.js). Zwraca { printed, ... } gdzie printed to:
 *   'yes'     -> lasttransaction POTWIERDZIL nasz paragon (state close/closed + printoutno).
 *                Na pewno wyszedl -> mozna fiskalizowac.
 *   'no'      -> drukarka odpowiedziala, ale paragonu NIE ma (close odrzucony <error>,
 *                albo transakcja cancelled). Na pewno NIE wyszedl -> bezpieczne ponowienie.
 *   'unknown' -> nie udalo sie dopytac nawet po reopen portu, albo drukarka nie wspiera
 *                diagnostyki lasttransaction. Niepewne -> NIE fiskalizuj, sprawdz drukarke.
 *
 * Pola dodatkowe: printoutno?, transactionNumber?, errorCode?, errorMessage?, retryable?.
 *
 * NIGDY nie zwracamy 'yes' bez pozytywnego potwierdzenia z lasttransaction (koniec FAIL-OPEN).
 * Pojedynczy timeout traktujemy jako stan przejsciowy i ponawiamy (patrz queryLastTransaction),
 * a przy zerwaniu portu probujemy go otworzyc ponownie (ensurePortWritable) i dopytac dalej.
 *
 * @param {object} options
 * @param {string} [options.expectedTotal] - suma fiskalna naszego paragonu (do rozroznienia
 *   od poprzedniego paragonu, gdy close zerwal polaczenie).
 * @param {Error}  [options.closeError]    - wyjatek z <receipt close>, jesli wystapil.
 */
// Fallback potwierdzenia gdy drukarka nie obsluguje <info action="lasttransaction">:
// porownujemy licznik paragonow (lastreceipt/receiptcount) z wartoscia zapamietana
// po POPRZEDNIM druku. Wzrost licznika = nasz paragon fizycznie wyszedl.
// Baseline trzymamy w localStorage (CAP), wiec NIE wysylamy zadnej komendy przed drukiem.
// Zwraca obiekt wyniku albo null, gdy nie da sie potwierdzic (wtedy zostaje 'unknown').
async function verifyByReceiptCounter(port, decoder) {
  // Kolejnosc wg protokolu Novitus XML:
  //  1) <enq/> (sekcja 2) - lasttransactioncorrect="yes" + intransaction="no" oznacza
  //     "ostatnia transakcja sfinalizowana poprawnie". To komenda BAZOWA, obslugiwana
  //     przez kazda drukarke (nasza odpowiada nawet na <dle/>), wiec probujemy ja pierwsza.
  //  2) <dle2/> (sekcja 3) - receiptcount, licznik paragonow (bez problematycznego
  //     <info action="last_daily_report">, ktory Deon 1.40 potrafi odrzucic).
  //  3) last_daily_report - dopiero na koncu.
  const enq = await queryEnq(port, decoder);
  if (enq && enq.intransaction === false && enq.lasttransactcorrect === true) {
    console.log('[FISCAL-VERIFY] === CONFIRMED via ENQ lasttransactioncorrect ===');
    return { printed: 'yes' };
  }
  if (enq && enq.intransaction === true) {
    // Transakcja wciaz otwarta -> paragon NIE zostal zamkniety ani wydrukowany.
    console.warn('[FISCAL-VERIFY] ENQ: intransaction=yes -> paragon niezamkniety');
    return null;
  }

  const dle2 = await queryDle2(port, decoder);
  if (dle2 && Number.isFinite(dle2.receiptcount)) {
    const prevD = getCap('dle2ReceiptCount');
    setCap('dle2ReceiptCount', dle2.receiptcount);
    if (Number.isFinite(prevD) && dle2.receiptcount > prevD) {
      console.log('[FISCAL-VERIFY] === CONFIRMED via DLE2 receiptcount ===', { prevD, now: dle2.receiptcount });
      return { printed: 'yes', printoutno: String(dle2.receiptcount) };
    }
    console.warn('[FISCAL-VERIFY] DLE2 licznik bez potwierdzenia', { prevD, now: dle2.receiptcount });
  }

  const counter = await queryLastReceiptCounter(port, decoder);
  if (!counter) return null;
  const current = counter.receiptcount ?? counter.lastreceipt;
  if (!Number.isFinite(current)) return null;

  const prev = getCap('receiptCounterValue');
  // Zawsze zapamietaj biezaca wartosc jako baseline dla kolejnego paragonu.
  setCap('receiptCounterValue', current);

  if (!Number.isFinite(prev)) {
    // Pierwszy pomiar - brak punktu odniesienia, nie potwierdzamy pozytywnie.
    console.warn('[FISCAL-VERIFY] licznik: brak baseline, zapisano', current);
    return null;
  }
  if (current > prev) {
    console.log('[FISCAL-VERIFY] === CONFIRMED via licznik paragonow ===', { prev, current });
    return { printed: 'yes', printoutno: String(current) };
  }
  // Licznik sie NIE zmienil -> paragon nie wyszedl. To definitywna informacja,
  // ale zostawiamy 'unknown' (bezpieczniej niz 'no': nie kusimy do auto-ponowienia,
  // gdy drukarka mogla nie odswiezyc licznika).
  console.warn('[FISCAL-VERIFY] licznik bez zmian', { prev, current });
  return null;
}

async function verifyReceiptPrintedXML(port, decoder, options = {}) {
  const maxRetries = options.maxRetries ?? 4;
  const retryDelayMs = options.retryDelayMs ?? 400;
  const expectedTotal = options.expectedTotal;
  const closeError = options.closeError;

  console.log('[FISCAL-VERIFY] === START verification ===', { hasCloseError: !!closeError });

  // Twarde odrzucenie <receipt close> (<error code=...>) = paragon NIE zostal zamkniety
  // ani wydrukowany. Definitywne 'no', bezpieczne ponowienie.
  if (closeError && /odrzuci/i.test(closeError.message || '')) {
    console.warn('[FISCAL-VERIFY] close rejected by printer -> printed=no');
    return {
      printed: 'no',
      retryable: true,
      errorCode: extractErrorCode(closeError.message),
      errorMessage: closeError.message,
    };
  }

  await new Promise(r => setTimeout(r, 400));

  let sawTransient = false;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    console.log(`[FISCAL-VERIFY] lasttransaction attempt ${attempt + 1}/${maxRetries}`);

    // Przed zapytaniem upewnij sie, ze port pisze; jak nie -> sprobuj otworzyc ponownie
    // (getPorts() pamieta nadany port, bez pytania uzytkownika).
    if (!port || !port.readable || !port.writable) {
      const reopened = await ensurePortWritable(port).catch(() => null);
      if (reopened) {
        port = reopened;
        console.log('[FISCAL-VERIFY] port reopened for verification');
      } else {
        sawTransient = true;
        await new Promise(r => setTimeout(r, retryDelayMs));
        continue;
      }
    }

    const tx = await queryLastTransaction(port, decoder);

    if (tx === null) {
      // Przejsciowe (timeout / chwilowe zerwanie) - ponawiamy.
      sawTransient = true;
      await new Promise(r => setTimeout(r, retryDelayMs));
      continue;
    }

    if (tx.unsupported) {
      // Drukarka nie potrafi potwierdzic przez lasttransaction -> sprobuj DRUGA metoda:
      // licznik paragonow (last_daily_report). Pytamy PO druku (nigdy przed - niektore
      // Deony 1.40 pokazuja wtedy blad parametru przed rozpoczeciem wydruku).
      console.warn('[FISCAL-VERIFY] lasttransaction unsupported -> probuje licznik paragonow');
      const byCounter = await verifyByReceiptCounter(port, decoder);
      if (byCounter) return byCounter;
      break;
    }

    if ((tx.state === 'close' || tx.state === 'closed') && tx.printoutno) {
      // Jesli close zerwal polaczenie, "ostatnia transakcja" moze byc POPRZEDNIM paragonem.
      // W takim wypadku wymagamy zgodnosci kwoty, zeby nie potwierdzic cudzego wydruku.
      if (closeError && expectedTotal && !totalsMatch(tx.total, expectedTotal)) {
        console.warn('[FISCAL-VERIFY] closed tx total mismatch after close error -> unknown');
        return {
          printed: 'unknown',
          retryable: false,
          errorMessage: 'Zerwano polaczenie przy zamykaniu paragonu, a drukarka raportuje inny paragon - sprawdz drukarke przed ponowieniem',
        };
      }
      console.log('[FISCAL-VERIFY] === CONFIRMED via lasttransaction ===', { printoutno: tx.printoutno });
      return {
        printed: 'yes',
        printoutno: tx.printoutno,
        transactionNumber: tx.number,
      };
    }

    if (tx.state === 'open') {
      // Druk w toku - poczekaj i sprobuj ponownie.
      console.log('[FISCAL-VERIFY] tx state=open, czekam...');
      await new Promise(r => setTimeout(r, retryDelayMs));
      continue;
    }

    if (tx.state === 'cancelled') {
      // Drukarka odpowiedziala: ostatnia transakcja anulowana -> paragon NIE wyszedl.
      console.warn('[FISCAL-VERIFY] tx cancelled -> printed=no');
      return {
        printed: 'no',
        retryable: true,
        errorMessage: 'Drukarka anulowala transakcje - paragon NIE zostal wydrukowany',
      };
    }

    // Nieznany/pusty state - jeszcze raz.
    await new Promise(r => setTimeout(r, retryDelayMs));
  }

  // Petla wyczerpana bez definitywnego potwierdzenia.
  if (closeError) {
    // Close sie nie powiodl (zerwanie), a my nie zdolalismy potwierdzic -> niepewne.
    console.warn('[FISCAL-VERIFY] close error + brak potwierdzenia -> unknown');
    return {
      printed: 'unknown',
      retryable: false,
      errorMessage: 'Zerwano polaczenie podczas zamykania paragonu - nie potwierdzono wydruku, sprawdz drukarke',
    };
  }
  if (sawTransient) {
    // Nie udalo sie dopytac drukarki nawet po reopen -> niepewne.
    console.warn('[FISCAL-VERIFY] transient failures, brak odpowiedzi -> unknown');
    return {
      printed: 'unknown',
      retryable: false,
      errorMessage: 'Nie udalo sie potwierdzic wydruku - drukarka nie odpowiada, sprawdz przed ponowieniem',
    };
  }
  // Drukarka odpowiadala, ale nie wspiera lasttransaction -> ostatnia szansa: licznik paragonow.
  const byCounterFinal = await verifyByReceiptCounter(port, decoder);
  if (byCounterFinal) return byCounterFinal;
  console.warn('[FISCAL-VERIFY] === UNKNOWN (brak diagnostyki) ===');
  return {
    printed: 'unknown',
    retryable: false,
    errorMessage: 'Drukarka nie potwierdza wydruku przez diagnostyke - sprawdz wzrokowo przed ponowieniem',
  };
}

/**
 * Wylicz sume paragonu (pozycje po rabacie + kaucje) DOKLADNIE ta sama
 * arytmetyka, ktora potem buduje pakiety w printReceiptXML. Uzywane przez
 * bramke kontrolna kwot PRZED wyslaniem czegokolwiek do drukarki.
 */
function policzSumeParagonu(receipt) {
  const allItems = receipt.items || [];
  let sumaGr = 0;
  for (const item of allItems) {
    if (isDepositItem(item)) {
      const qty = Math.max(1, Math.round(parseFloat(item.quantity)));
      const price = parseFloat(item.price);
      sumaGr += Math.round(Math.round(price * qty * 100) / 100 * 100);
      continue;
    }
    const qty = parseFloat(item.quantity);
    if (!(qty > 0)) continue;
    const unitPrice = Math.round(parseFloat(item.price) * 100) / 100;
    const lineTotal = Math.round(unitPrice * qty * 100) / 100;
    let itemDiscount = item.discount ? parseFloat(item.discount.amount) || 0 : 0;
    if (itemDiscount < 0) itemDiscount = 0;
    if (itemDiscount > lineTotal) itemDiscount = lineTotal;
    sumaGr += Math.round(Math.round((lineTotal - itemDiscount) * 100) / 100 * 100);
  }
  return sumaGr / 100;
}

/**
 * Print a fiscal receipt using the XML protocol.
 * Sends each step as a separate packet for clear error identification.
 */
export async function printReceiptXML(port, decoder, receipt) {
  // === BRAMKA KONTROLNA KWOT (ustalenie A wlasciciela) ===
  // Gdy wolajacy przekaze expected_total (kwota z finalizacji / naglowka
  // transakcji, czyli to, co klient FAKTYCZNIE zaplacil), suma wyliczona
  // z pozycji musi zgadzac sie z nia CO DO GROSZA. Rozjazd = STOP druku
  // ZANIM jakikolwiek bajt pojdzie na port - zly paragon na papierze jest
  // gorszy niz brak paragonu, ktory kasjerka widzi i moze ponowic.
  // Obie strony licza rabat tym samym silnikiem, wiec rozjazd to prawdziwy
  // blad danych, nie codzienna roznica zaokraglen.
  const expectedTotal = parseFloat(receipt.expected_total);
  if (Number.isFinite(expectedTotal)) {
    const sumaPozycji = policzSumeParagonu(receipt);
    if (Math.round(sumaPozycji * 100) !== Math.round(expectedTotal * 100)) {
      const opis = `Kwoty niezgodne: pozycje paragonu daja ${sumaPozycji.toFixed(2)} zl, `
        + `a transakcja jest na ${expectedTotal.toFixed(2)} zl. Nie drukuje. `
        + 'Sprawdz transakcje i wydrukuj paragon ponownie z listy transakcji; '
        + 'jesli problem wraca - zglos to kierownikowi.';
      console.error('[FISCAL] STOP druku - rozjazd kwot:', sumaPozycji, 'vs', expectedTotal);
      return {
        printed: 'no',
        success: false,
        retryable: false,
        nieWyslano: true,
        errorCode: 'KWOTY_NIEZGODNE',
        error: opis,
        errorMessage: opis,
      };
    }
  }

  // Port jest zasobem WYLACZNYM: wstrzymaj heartbeat na czas druku (endPrinting w finally).
  await beginPrinting();
  try {
    // Przed drukiem: sprawdz czy port jest zapisywalny; jesli nie -> sprobuj reopen
    // (getPorts() pamieta nadany port, bez pytania uzytkownika).
    if (!port || !port.readable || !port.writable) {
      const reopened = await ensurePortWritable(port).catch(() => null);
      if (reopened) port = reopened;
    }

    // Oprozni bufor z ewentualnej spoznionej odpowiedzi <dle> (heartbeat), zeby
    // odczyt potwierdzenia paragonu nie zlapal starego <dle> i nie uznal paragonu
    // za niezafiskalizowany. (Usunieto tez zbedny pre-check getStatusXML: sprawdzal
    // printerStatus.inTransaction, ktorego getStatusXML nie ustawia - martwy kod,
    // a wysylal kolejny <dle/> zasmiecajacy bufor. Otwarta transakcje i tak kasuje
    // bezwarunkowy "Step 0: cancel" ponizej.)
    await drainPort(port, 300);

    const allItems = receipt.items || [];
    const fiscalItemsRaw = allItems.filter(item => !isDepositItem(item));
    const depositItems   = allItems.filter(item =>  isDepositItem(item));
    const fiscalItems    = prepareItemNames(fiscalItemsRaw, 40);

    // Step 0: cancel any previous transaction
    try {
      await sendXML(port, decoder, '<packet><receipt action="cancel"></receipt></packet>', false);
    } catch (_) { /* OK if nothing to cancel */ }
    await new Promise(r => setTimeout(r, 150));

    // UWAGA: pre-counter <info action="last_daily_report"/> zostal USUNIETY -
    // niektore drukarki Deon Online 1.40 odrzucaja te komende z "blad parametru"
    // wyswietlanym PRZED rozpoczeciem druku. lasttransaction po close i tak wystarczy.

    // Step 1: open receipt
    await sendXML(port, decoder, '<packet><receipt action="begin" mode="online"></receipt></packet>');
    await new Promise(r => setTimeout(r, 150));

    // Step 2: add fiscal items
    // UWAGA: rabat per pozycja MUSI byc zagniezdzony w <item>...</item> w jednym pakiecie
    // (Novitus XML protocol sekcja 12: "Option of giving discount/markup to the chosen
    // position must be placed in tag <item>"). Wysylanie standalone <discount> po <item/>
    // sprawia ze drukarka traktuje go jako rabat subtotal (sekcja 14, default type),
    // co powoduje rozjazd kwot ~8.247% zamiast 10% per linia.
    let calculatedTotal = 0;
    for (let i = 0; i < fiscalItems.length; i++) {
      const item = fiscalItems[i];
      const name = item.fiscalName;
      // ILOSC: obsluga sprzedazy na wage / ulamki. NIE zaokraglamy do calkowitej
      // (dawne Math.max(1, Math.round(...)) drukowalo zla ilosc/kwote dla np. 0.5 kg).
      // Wymagamy jedynie poprawnej, dodatniej wartosci.
      const qty = parseFloat(item.quantity);
      if (!(qty > 0)) {
        console.warn('[FISCAL] pomijam pozycje z niepoprawna iloscia:', item.quantity, name);
        continue;
      }
      const vatLetter = mapVatRate(item.vat_rate);
      const unitPrice = Math.round(parseFloat(item.price) * 100) / 100;
      const lineTotal = Math.round(unitPrice * qty * 100) / 100;
      // RABAT KWOTOWY pozycji: nie moze byc ujemny (byloby to podbicie ceny) ani
      // >= wartosci linii. Przycinamy do [0, lineTotal).
      let itemDiscount = item.discount ? parseFloat(item.discount.amount) || 0 : 0;
      if (itemDiscount < 0) itemDiscount = 0;
      if (itemDiscount > lineTotal) itemDiscount = lineTotal;
      const discountName = item.discount?.name || 'Rabat';
      const lineTotalAfterDiscount = Math.round((lineTotal - itemDiscount) * 100) / 100;
      calculatedTotal += lineTotalAfterDiscount;

      let discountFragment = '';
      if (itemDiscount > 0) {
        const discountNameClean = discountName.substring(0, 20).replace(/[<>&"']/g, '');
        discountFragment = `<discount name="${discountNameClean}" value="${itemDiscount.toFixed(2)}" action="discount"></discount>`;
      }

      const itemXml = `<packet><item name="${name}" quantity="${qty}" quantityunit="szt" ptu="${vatLetter}" price="${unitPrice.toFixed(2)}" recipe="" charge="" plu="" description="" action="sale">${discountFragment}</item></packet>`;
      await sendXML(port, decoder, itemXml);
      await new Promise(r => setTimeout(r, itemDiscount > 0 ? 300 : 150));
    }

    // Step 2.5: deposits as <container type="out">
    let depositTotal = 0;
    for (const deposit of depositItems) {
      const qty = Math.max(1, Math.round(parseFloat(deposit.quantity)));
      const price = parseFloat(deposit.price);
      const lineTotal = Math.round(price * qty * 100) / 100;
      depositTotal += lineTotal;
      const containerXml = `<packet><container action="sale" price="${price.toFixed(2)}" type="out" quantity="${qty}"></container></packet>`;
      await sendXML(port, decoder, containerXml);
      await new Promise(r => setTimeout(r, 300));
    }

    // Suma fiskalna (bez kaucji) — do total w receipt close
    const fiscalTotal = calculatedTotal.toFixed(2);
    // Suma platnosci (produkty + kaucje) — tyle klient placi
    const paymentTotal = (calculatedTotal + depositTotal).toFixed(2);
    calculatedTotal = calculatedTotal.toFixed(2);

    // Step 3: payments
    // Kwota platnosci musi pokryc produkty + kaucje, ale total paragonu = tylko fiskalne
    const payments = receipt.payments || [];
    if (payments.length > 0) {
      // Platnosc dzielona — kwoty juz zawieraja kaucje (z frontendu)
      for (let i = 0; i < payments.length; i++) {
        const payment = payments[i];
        const paymentInfo = mapPaymentType(payment.type || payment.payment_type);
        const paymentAmount = parseFloat(payment.amount || payment.value || 0).toFixed(2);
        if (parseFloat(paymentAmount) > 0) {
          const paymentXml = `<packet><payment type="${paymentInfo.type}" action="add" value="${paymentAmount}" name="${paymentInfo.name}"></payment></packet>`;
          await sendXML(port, decoder, paymentXml);
          await new Promise(r => setTimeout(r, 300));
        }
      }
    } else {
      // Jedna metoda platnosci — wyslij pelna kwote (produkty + kaucje)
      // zeby drukarka nie przypisywala kaucji do gotowki
      const paymentInfo = mapPaymentType(receipt.payment_type);
      const paymentXml = `<packet><payment type="${paymentInfo.type}" action="add" value="${paymentTotal}" name="${paymentInfo.name}"></payment></packet>`;
      await sendXML(port, decoder, paymentXml);
      await new Promise(r => setTimeout(r, 300));
    }

    // Step 4: close receipt
    // UWAGA: total = suma FISKALNA (bez kaucji) — tak wymaga Novitus
    // Kaucje sa rozliczane osobno przez <container>
    // UWAGA: atrybut paymenttype NIE istnieje w spec XML v1.09 - platnosci sa wylacznie
    // przez <payment> packets wyzej. Usunelismy paymenttype zeby uniknac odrzucenia
    // przez firmware ktore strikte parsuja XML.
    let cashier = stripDiacritics((receipt.cashier || 'Kasjer').substring(0, 20));
    const nipValue = receipt.nip_nabywcy ? receipt.nip_nabywcy.replace(/[^0-9\-]/g, '').substring(0, 16) : '';
    const documentNumber = receipt.document_number || receipt.documentNumber || receipt.transaction_id || '';

    let closeXml = `<packet><receipt action="close" systemno="${documentNumber}" checkout="1" cashier="${cashier}" total="${fiscalTotal}"`;
    if (nipValue) {
      closeXml += ` nip="${nipValue}"`;
    }
    closeXml += `/></packet>`;

    // Step 4b: close — druk fizyczny trwa kilka sekund, wiec dajemy DLUGIE okno odczytu
    // (idleGap konczy je zaraz po nadejsciu odpowiedzi). Wyjatku NIE wypuszczamy do
    // outer-catch: przekazujemy go do weryfikacji, ktora rozstrzygnie no/unknown.
    let closeError = null;
    try {
      await sendXML(port, decoder, closeXml, true, { responseTimeout: 6000, idleGap: 250 });
    } catch (e) {
      closeError = e;
      console.warn('[FISCAL] close error (przekazuje do weryfikacji):', e?.message);
    }

    // Step 5: AUTO-WERYFIKACJA wydruku wg KONTRAKTU (printed yes/no/unknown).
    // Zamiast pytac kasjera "czy paragon wyszedl", odpytujemy drukarke; przy zerwaniu
    // portu weryfikacja sama go otwiera ponownie i dopytuje lasttransaction.
    // Po wyslaniu close nie wolno zdegradowac wyniku do 'no' (co znaczy "na pewno
    // nie wyszedl" -> ponowny druk = podwojny paragon). Nieoczekiwany blad weryfikacji
    // po close traktujemy jako 'unknown'.
    let verification;
    try {
      verification = await verifyReceiptPrintedXML(port, decoder, {
        expectedTotal: fiscalTotal,
        closeError,
      });
    } catch (verifyErr) {
      console.error('[FISCAL] blad weryfikacji po close:', verifyErr?.message);
      verification = {
        printed: 'unknown',
        retryable: false,
        errorMessage: 'Nie udalo sie zweryfikowac wydruku po zamknieciu paragonu - sprawdz drukarke przed ponowieniem',
      };
    }

    return {
      printed: verification.printed,                 // 'yes' | 'no' | 'unknown'
      printoutno: verification.printoutno,
      transactionNumber: verification.transactionNumber,
      errorCode: verification.errorCode,
      errorMessage: verification.errorMessage,
      retryable: verification.retryable,
      // back-compat dla starszych call-site (np. FiscalPrinterTab): success == potwierdzony wydruk
      success: verification.printed === 'yes',
      error: verification.printed === 'yes' ? undefined : verification.errorMessage,
    };

  } catch (error) {
    // Blad na etapie begin/item/payment -> paragon NIE zostal zamkniety ani wydrukowany.
    // To bezpieczny przypadek do ponowienia (printed='no').
    console.error('Drukarka [XML]: blad drukowania:', error);
    try {
      await sendXML(port, decoder, '<packet><receipt action="cancel"></receipt></packet>', false);
    } catch (_) {}
    return {
      printed: 'no',
      success: false,
      retryable: true,
      error: error.message,
      errorMessage: error.message,
      errorCode: extractErrorCode(error.message),
    };
  } finally {
    endPrinting();
  }
}

/**
 * SCIEZKA KONTRAKTOWA (kontrakt 1.0): drukuje paragon ZBUDOWANY PRZEZ SERWER.
 *
 * Roznica wzgledem printReceiptXML() jest jedna, ale zasadnicza: tu nie ma ani
 * jednego dzialania arytmetycznego. Wszystkie kwoty, ilosci, litery stawek,
 * nazwy fiskalne i rozdzial rabatu przyszly z serwera jako gotowe ciagi znakow.
 * Przegladarka jest kablem: sklada pakiety, wysyla, czyta odpowiedzi, nagrywa
 * transkrypt i oddaje go do zapisu.
 *
 * Wysylane bajty sa IDENTYCZNE z tymi ze sciezki starej - poza trzema bledami,
 * ktore ta zmiana naprawia (rabat z aktualnego koszyka, prawdziwa nazwa pozycji,
 * arytmetyka groszowa). Szablony pakietow siedza w receiptContract.js.
 *
 * @param {object} kontrakt - odpowiedz POST /api/transactions/<id>/fiscal-receipt (pole data)
 * @returns {object} { printed: 'yes'|'no'|'unknown', ..., transkrypt, pakietow, bajtowWyslanych }
 */
export async function printReceiptFromContractXML(port, decoder, kontrakt) {
  // Bramka wejsciowa: niekompletny albo obcy wersja kontrakt = NIE DRUKUJEMY.
  // Nie ma tu awaryjnego "policz sobie sam" - to bylby powrot do bledu zrodlowego.
  const kontrola = sprawdzKontrakt(kontrakt);
  if (!kontrola.ok) {
    console.error('[FISCAL-KONTRAKT] odmowa druku:', kontrola.kod, kontrola.blad);
    return {
      printed: 'no',
      success: false,
      retryable: true,
      nieWyslano: true,          // nic nie poszlo na port - transakcja bez sladu w drukarce
      errorCode: kontrola.kod,
      error: kontrola.blad,
      errorMessage: kontrola.blad,
      transkrypt: [],
      pakietow: 0,
      bajtowWyslanych: 0,
      czasDrukuMs: 0,
    };
  }

  const kroki = zbudujPakiety(kontrakt);
  const transkrypt = [];
  const start = Date.now();
  let pakietow = 0;
  let bajtowWyslanych = 0;

  const zapisz = (kier, xml) => {
    transkrypt.push({ i: transkrypt.length + 1, kier, t_ms: Date.now() - start, xml: String(xml) });
  };

  // Port jest zasobem WYLACZNYM: wstrzymaj heartbeat na czas druku (endPrinting w finally).
  await beginPrinting();
  try {
    if (!port || !port.readable || !port.writable) {
      const reopened = await ensurePortWritable(port).catch(() => null);
      if (reopened) port = reopened;
    }

    // Oprozni bufor ze spoznionej odpowiedzi <dle> (heartbeat), zeby odczyt
    // potwierdzenia paragonu nie zlapal starego <dle>.
    await drainPort(port, 300);

    let closeError = null;

    for (const krok of kroki) {
      zapisz('tx', krok.xml);
      pakietow += 1;
      bajtowWyslanych += textToBytes(krok.xml).length;

      try {
        const odpowiedz = await sendXML(port, decoder, krok.xml, krok.czytaj, krok.opcje || {});
        if (odpowiedz) zapisz('rx', odpowiedz);
      } catch (e) {
        zapisz('err', `${krok.krok}: ${e?.message || e}`);
        if (krok.ignorujBlad) {
          // cancel na pustej drukarce - normalne, idziemy dalej.
        } else if (krok.przekazBlad) {
          // close: wyjatku NIE wypuszczamy - rozstrzygnie go weryfikacja (no/unknown).
          closeError = e;
          console.warn('[FISCAL-KONTRAKT] close error (przekazuje do weryfikacji):', e?.message);
        } else {
          throw e;
        }
      }

      if (krok.czekajMs > 0) {
        await new Promise(r => setTimeout(r, krok.czekajMs));
      }
    }

    // AUTO-WERYFIKACJA wydruku - ta sama funkcja i te same trzy stany co dotad
    // (yes / no / unknown). Nie wolno jej uproscic do "chyba wyszlo".
    let verification;
    try {
      verification = await verifyReceiptPrintedXML(port, decoder, {
        expectedTotal: kontrakt.sumy.suma_fiskalna,
        closeError,
      });
    } catch (verifyErr) {
      console.error('[FISCAL-KONTRAKT] blad weryfikacji po close:', verifyErr?.message);
      verification = {
        printed: 'unknown',
        retryable: false,
        errorMessage: 'Nie udalo sie zweryfikowac wydruku po zamknieciu paragonu - sprawdz drukarke przed ponowieniem',
      };
    }
    zapisz('log', `weryfikacja: printed=${verification.printed}`
      + (verification.printoutno ? ` printoutno=${verification.printoutno}` : '')
      + (verification.transactionNumber ? ` number=${verification.transactionNumber}` : '')
      + (verification.errorCode ? ` kod=${verification.errorCode}` : ''));

    return {
      printed: verification.printed,                 // 'yes' | 'no' | 'unknown'
      printoutno: verification.printoutno,
      transactionNumber: verification.transactionNumber,
      errorCode: verification.errorCode,
      errorMessage: verification.errorMessage,
      retryable: verification.retryable,
      success: verification.printed === 'yes',
      error: verification.printed === 'yes' ? undefined : verification.errorMessage,
      // Material do ZAPISU 2 (raport z kabla).
      transkrypt,
      pakietow,
      bajtowWyslanych,
      czasDrukuMs: Date.now() - start,
      payloadHash: kontrakt.kontrola?.payload_hash,
      wydrukId: kontrakt.wydruk_id ?? null,
    };

  } catch (error) {
    // Blad na etapie begin/item/payment -> paragon NIE zostal zamkniety ani
    // wydrukowany. Bezpieczny przypadek do ponowienia (printed='no').
    console.error('[FISCAL-KONTRAKT] blad drukowania:', error);
    const xmlCancel = '<packet><receipt action="cancel"></receipt></packet>';
    try {
      zapisz('tx', xmlCancel);
      pakietow += 1;
      bajtowWyslanych += textToBytes(xmlCancel).length;
      await sendXML(port, decoder, xmlCancel, false);
    } catch (_) {}
    return {
      printed: 'no',
      success: false,
      retryable: true,
      error: error.message,
      errorMessage: error.message,
      errorCode: extractErrorCode(error.message),
      transkrypt,
      pakietow,
      bajtowWyslanych,
      czasDrukuMs: Date.now() - start,
      payloadHash: kontrakt.kontrola?.payload_hash,
      wydrukId: kontrakt.wydruk_id ?? null,
    };
  } finally {
    endPrinting();
  }
}

/**
 * Print daily (Z) report via XML protocol.
 */
export async function printDailyReportXML(port, decoder) {
  const today = new Date();
  const dateStr = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${today.getFullYear()}`;
  const xml = `<packet>\n<report type="daily" date="${dateStr}"></report>\n</packet>`;
  await sendXML(port, decoder, xml);
}

/**
 * Open cash drawer via XML protocol.
 */
export async function openDrawerXML(port, decoder) {
  const xml = `<packet>\n<control action="drawer"></control>\n</packet>`;
  await sendXML(port, decoder, xml);
}

/**
 * Cancel the current open transaction via XML.
 */
export async function cancelTransactionXML(port, decoder) {
  const xml = `<packet>\n<receipt action="cancel"></receipt>\n</packet>`;
  await sendXML(port, decoder, xml);
}

/**
 * Print a non-fiscal document via XML protocol (Novitus XML v1.02, section 68).
 */
export async function printNonFiscalDocumentXML(port, decoder, documentData) {
  const { title = 'DOKUMENT', lines = [], titleBig = false } = documentData;

  let linesXml = '';

  if (title) {
    let attrs = 'type="line" fontid="1" center="yes"';
    if (titleBig) attrs += ' fontattr="big"';
    linesXml += `<line ${attrs}>${escapeXmlLocal(title)}</line>\n`;
    linesXml += `<line type="underline"></line>\n`;
  }

  for (const line of lines) {
    if (typeof line === 'string') {
      const lineText = escapeXmlLocal(line.substring(0, 42));
      linesXml += `<line type="line" fontid="1">${lineText}</line>\n`;
    } else if (typeof line === 'object' && line !== null) {
      const lineText = escapeXmlLocal(String(line.text || '').substring(0, 42));
      let attrs = 'type="line" fontid="1"';
      if (line.big)  attrs += ' fontattr="big"';
      else if (line.high) attrs += ' fontattr="high"';
      if (line.bold)   attrs += ' bold="yes"';
      if (line.center) attrs += ' center="yes"';
      if (line.underlineBefore) linesXml += `<line type="underline"></line>\n`;
      if (lineText || line.spacer) {
        if (line.spacer) {
          linesXml += `<line type="line" fontid="1"> </line>\n`;
        } else {
          linesXml += `<line ${attrs}>${lineText}</line>\n`;
        }
      }
      if (line.underlineAfter) linesXml += `<line type="underline"></line>\n`;
    }
  }

  const xml = `<packet>\n<nonfiscalprintout nonfiscalheader="yes">\n${linesXml}</nonfiscalprintout>\n</packet>`;
  await sendXML(port, decoder, xml);
  return { success: true, message: 'Dokument niefiskalny wydrukowany' };
}

// Local helper to avoid importing escapeXml (circular)
function escapeXmlLocal(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}
